import { useEffect } from "react";
import { isNativeApp } from "@/lib/platform";

interface AdSlotProps {
  slot: string; // AdSense ad slot ID
  format?: "auto" | "fluid" | "rectangle" | "vertical" | "horizontal";
  responsive?: boolean;
  className?: string;
}

export const AdSlot = ({
  slot,
  format = "auto",
  responsive = true,
  className = ""
}: AdSlotProps) => {

  // Never load or render AdSense in the native app (App Store compliance —
  // no web ad SDKs in the wrapped build). Computed before the effect so both
  // the script-load path and the render path bail together.
  const native = isNativeApp();

  useEffect(() => {
    if (native) return;
    // Check if user has consented to advertising cookies
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) return;

    const preferences = JSON.parse(consent);
    if (!preferences.advertising) return;

    // Suppress Google's Funding Choices CMP — we handle consent ourselves.
    // Must be set BEFORE AdSense script loads. googlefc.controlledMessaging
    // tells Google's CMP: "consent already collected, don't show your dialog."
    const w = window as any;
    if (!w.googlefc) w.googlefc = {};
    if (!w.googlefc.controlledMessaging) {
      w.googlefc.controlledMessaging = Promise.resolve();
    }

    // Dynamically load AdSense script if not already present
    const CLIENT_ID = 'ca-pub-9512047279819133';
    if (!document.querySelector(`script[src*="${CLIENT_ID}"]`)) {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT_ID}`;
      s.crossOrigin = 'anonymous';
      document.head.appendChild(s);
    }

    // Push ad once script is ready
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      console.error("AdSense error:", err);
    }
  }, []);

  if (native) return null;

  // Check consent before rendering
  const consent = localStorage.getItem("cookie-consent");
  if (!consent) return null;

  const preferences = JSON.parse(consent);
  if (!preferences.advertising) {
    return (
      <div className={`border border-dashed border-matrix-green/20 rounded-lg p-4 text-center ${className}`}>
        <p className="text-xs text-muted-foreground">
          Enable advertising cookies to see relevant ads
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client="ca-pub-9512047279819133"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? "true" : "false"}
      />
    </div>
  );
};

// Extend window for AdSense
declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}
